import { Grid, Typography } from '@mui/material';
import { PageSelector, RecordsPerPageSelector, useAgGridPagination } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { BasicTableOptions } from '@tassat/common-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import moment from 'moment';
import React from 'react';

import Header from 'Containers/header';
import { convertDateFilterToTime } from 'Lib/agGridHelper';
import { DEFAULT_ITEMS_PER_PAGE } from 'Lib/constant';
import { exportAlerts } from 'Lib/exports/alertsExport';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { DateFilter } from 'SharedComponents/agGrid/filters/dateFilter/dateFilter';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';

import AlertsAgGrid from './alertAgGrid';

import type { AlertsProps } from '.';

export const PAGE_TITLE = 'Alerts';

const tableOptionsInitialState: BasicTableOptions = {
    PageNo: 1,
    RecordPerPage: DEFAULT_ITEMS_PER_PAGE,
    Sort: 'alertTime|DESC',
    DefaultSort: 'alertTime|DESC',
    SelectedDateFilter: '1d',
    DateCondition: '',
    TableFilters: [],
    SelectedFilter: 'none',
    FilterCondition: '',
    DefaultState: true
};

export function Alerts(props: AlertsProps) {
    const { classes } = props;

    const [ rowCount, setRowCount ] = React.useState<number>(0);
    const [ TableOptions, TableOptionsDispatch ] = React.useReducer<(TableOptions, { type, values }: { type: string; values: object }) =>
    BasicTableOptions>(tableOptionsReducer, {
            ...tableOptionsInitialState,
            DateCondition: `alertTime|GT|${moment()
                .subtract(1, 'day')
                .valueOf()}`
        });
    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [ setPageNum, setRecordsPerPage ] = useAgGridPagination(TableOptionsDispatch);

    React.useEffect(() => {
        fetchApiTransactions();
    }, [
        TableOptions.DateCondition,
        TableOptions.PageNo,
        TableOptions.RecordPerPage,
        TableOptions.FilterCondition,
        TableOptions.Sort
    ]);

    const selectTimeRange = (timeFrame: string) => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                SelectedDateFilter: timeFrame,
                DateCondition: `alertTime|GT|${convertDateFilterToTime(timeFrame)}`,
                PageNo: 1,
                FilterCondition: TableOptions.FilterCondition,
                TableFilters: TableOptions.TableFilters,
                SelectedFilter: 'none',
                DefaultsState: false
            }
        });
    };

    const clearFilter = () => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                ...tableOptionsInitialState,
                DateCondition: `alertTime|GT|${moment()
                    .subtract(1, 'day')
                    .valueOf()}`
            }
        });
    };

    const fetchApiTransactions = async () => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            DateCondition,
            FilterCondition
        } = TableOptions;

        if (!DateCondition || !PageNo) return;

        let Condition = '';
        if (FilterCondition) {
            Condition = `${DateCondition}:${FilterCondition}`;
        } else {
            Condition = `${DateCondition}`;
        }

        const res = await Worker.getInstance()
            .getAlerts({
                PageNo,
                RecordPerPage,
                Sort,
                Condition
            });

        setRowCount(res.data.TotalCount);

        return res;
    };

    const fetchAlertExports = async () => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            DateCondition,
            FilterCondition
        } = TableOptions;

        if (!DateCondition || !PageNo) return;

        let Condition = '';
        if (FilterCondition) {
            Condition = `${DateCondition}:${FilterCondition}`;
        } else {
            Condition = `${DateCondition}`;
        }

        const res = await Worker.getInstance()
            .getAlertsExport({
                PageNo,
                RecordPerPage,
                Sort,
                Condition
            });

        exportAlerts(res.data.alerts);
    };

    return (
        <TableOptionsContext.Provider value={tableContextMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Header />
                <Grid
                    className={classes.pageTitle}
                    item
                    xs={12}
                >
                    <PageHeaderTitle
                        title={PAGE_TITLE}
                    />
                </Grid>
                <Grid
                    className={classes.filterBar}
                    container
                    direction='row'
                    item
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                    xs={12}
                >
                    <Grid
                        alignItems='center'
                        className={classes.filterSelection}
                        container
                        direction='row'
                        item
                        spacing={0}
                        wrap='nowrap'
                    >
                        <DateFilter
                            selectTimeRange={selectTimeRange}
                            timeFrameinDays={TableOptions.SelectedDateFilter}
                        />
                        <Typography
                            className={classes.filterOption}
                            onClick={clearFilter}
                            variant='h5'
                        >
                            Clear Filter
                        </Typography>
                    </Grid>
                    <Grid
                        className={classes.exportButtonContainer}
                        item
                    >
                        <ToolbarButton
                            Icon={ExportIcon}
                            iconClassName={classes.exportIcon}
                            onClick={fetchAlertExports}
                            text='Export'
                        />
                    </Grid>
                </Grid>
                <Grid
                    className={classes.gridContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <AlertsAgGrid />
                </Grid>
                <Grid
                    className={classes.pageSelectorContainer}
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <RecordsPerPageSelector
                        currentRecordsPerPage={TableOptions.RecordPerPage}
                        setRecordsPerPage={setRecordsPerPage}
                    />
                    <PageSelector
                        changePageOnApplyOnly
                        currentPageNum={TableOptions.PageNo}
                        onPageChange={setPageNum}
                        recordsPerPage={TableOptions.RecordPerPage}
                        totalRows={rowCount}
                    />
                </Grid>
            </Grid>
        </TableOptionsContext.Provider>
    );
}